/**
 * AppICEMAP — AppShell
 * Root layout component: composes the header, map, bottom sheet, and FAB.
 *
 * Layout:
 *   ┌──────────────────────────┐
 *   │  Header (48px)           │
 *   ├──────────────────────────┤
 *   │                          │
 *   │  Map Viewport             │
 *   │                          │
 *   │              [FAB]       │
 *   ├──────────────────────────┤
 *   │  Bottom Sheet            │
 *   │   <report-list>          │
 *   └──────────────────────────┘
 */

import { state, subscribe, dispatch } from "../../store/index.js";
import { initTheme, onThemeChange } from "../../utils/theme.js";
import {
	start as startPoller,
	updateBounds,
	updateNearby,
	hasGpsAnchor,
} from "../../api/poller.js";
import { fetchCategories } from "../../api/categories.js";
import { readCache, writeCache, CACHE_KEYS } from "../../services/cache.js";
import { filterActive } from "../../utils/expiry.js";
import router from "../../router.js";

export class AppShell {
	/**
	 * Mount the app shell into the #app container.
	 */
	static async mount() {
		const appRoot = document.getElementById("app");
		if (!appRoot) {
			console.error("[AppShell] #app container not found");
			return;
		}

		// Clear loading placeholder
		appRoot.innerHTML = "";

		// Build shell DOM
		const shell = AppShell._buildDOM();
		appRoot.appendChild(shell);

		// Init theme
		initTheme();

		// Local-first: pintar la caché antes de tocar la red
		AppShell._hydrateFromCache();

		// Load categories
		AppShell._loadCategories();

		// Setup state subscriptions
		AppShell._setupSubscriptions();

		// Start polling
		startPoller();

		// Setup map events
		AppShell._setupMapEvents();

		// Setup search bar (moved above the map in the Nocturne layout)
		AppShell._setupSearch();

		// Setup FAB
		AppShell._setupFAB();

		// Setup report list event forwarding
		AppShell._setupReportListEvents();

		console.log("[AppShell] Mounted");
	}

	/* ── DOM Construction ──────────────────────────────────────────────── */

	static _buildDOM() {
		const wrapper = document.createElement("div");
		wrapper.className = "app-shell";

		wrapper.innerHTML = `
    		<!-- Header -->
    		<header class="app-header" role="banner">
    			<div class="app-header__logo">
    				<i class="ph ph-map-pin" aria-hidden="true"></i>
    				<span class="app-header__title">ICEMAP</span>
    			</div>
    		</header>

    		<!-- Offline Banner -->
    		<offline-banner></offline-banner>

    		<!-- Search -->
    		<div class="search-region">
    			<search-bar placeholder="Buscar reportes cerca de ti…"></search-bar>
    		</div>

    		<!-- Map card -->
    		<div class="map-card">
    			<map-viewport></map-viewport>
    		</div>

    		<!-- Report list (pills + cards) -->
    		<report-list></report-list>

    		<!-- FAB -->
    		<button class="fab" id="fab-create" aria-label="Nuevo reporte">
    			<i class="ph ph-plus" aria-hidden="true"></i>
    		</button>
    	`;

		return wrapper;
	}

	/* ── Categories ────────────────────────────────────────────────────── */

	static async _loadCategories() {
		try {
			const categories = await fetchCategories();
			if (categories.length > 0) {
				dispatch("SET_CATEGORIES", categories);
				writeCache(CACHE_KEYS.categories, categories);
			}
		} catch (err) {
			console.warn("[AppShell] Failed to load categories:", err);
		}
	}

	/**
	 * Paint whatever was cached on the previous run before any network call.
	 * The poller replaces it moments later; until then the user sees the app
	 * instead of an empty screen.
	 */
	static async _hydrateFromCache() {
		const [categories, reports] = await Promise.all([
			readCache(CACHE_KEYS.categories),
			readCache(CACHE_KEYS.reports),
		]);

		if (categories?.value?.length && state.categories.length === 0) {
			dispatch("SET_CATEGORIES", categories.value);
		}

		if (reports?.value?.length && state.reports.length === 0) {
			// Expired reports must not come back from the cache
			const vigentes = filterActive(reports.value);
			if (vigentes.length > 0) dispatch("SET_REPORTS", vigentes);
		}
	}

	/* ── Map Events ────────────────────────────────────────────────────── */

	static _setupMapEvents() {
		const mapViewport = document.querySelector("map-viewport");
		if (!mapViewport) return;

		mapViewport.addEventListener(
			"map:ready",
			() => {
				// The map lives in a flex card — make Leaflet re-measure once laid out.
				mapViewport.invalidateSize?.();
				const bounds = mapViewport.getBounds();
				updateBounds(bounds, state.filters.selectedCategories);

				AppShell._anchorNearbyOnMap(bounds);
			},
			{ once: true },
		);

		mapViewport.addEventListener("map:viewport-changed", (e) => {
			const { bounds } = e.detail;
			updateBounds(bounds, state.filters.selectedCategories);
			AppShell._anchorNearbyOnMap(bounds);
		});

		mapViewport.addEventListener("map:report-clicked", (e) => {
			const { reportId } = e.detail;
			dispatch("SELECT_REPORT", reportId);
			router.navigate(`/detail/${reportId}`);
		});
	}

	/**
	 * Without a GPS fix there is no "near you", so the nearby query falls back
	 * to the centre of the map. As soon as a real position arrives it takes
	 * over and panning stops moving the anchor.
	 *
	 * @param {{ sw: [number, number], ne: [number, number] } | null} bounds
	 */
	static _anchorNearbyOnMap(bounds) {
		if (!bounds || hasGpsAnchor()) return;

		const center = [
			(bounds.sw[0] + bounds.ne[0]) / 2,
			(bounds.sw[1] + bounds.ne[1]) / 2,
		];
		updateNearby(center, state.filters?.radiusKm ?? null);
	}

	/* ── Search ────────────────────────────────────────────────────────── */

	static _setupSearch() {
		const searchBar = document.querySelector(".search-region search-bar");
		if (!searchBar) return;

		searchBar.addEventListener("search-bar:input", (e) => {
			dispatch("SET_FILTER", { searchText: e.detail.value });
		});
	}

	/* ── FAB ───────────────────────────────────────────────────────────── */

	static _setupFAB() {
		const fab = document.getElementById("fab-create");
		if (!fab) return;

		fab.addEventListener("click", () => {
			router.navigate("/create");
		});

		// Disable FAB when offline; auth check is handled on click
		subscribe("offline", (offline) => {
			if (offline) {
				fab.setAttribute("disabled", "");
				fab.setAttribute("aria-label", "Sin conexi�n");
			} else {
				fab.removeAttribute("disabled");
				fab.setAttribute("aria-label", "Nuevo reporte");
			}
		});
	}

	/* ── Report List Events ────────────────────────────────────────────── */

	static _setupReportListEvents() {
		const reportList = document.querySelector("report-list");
		if (!reportList) return;

		reportList.addEventListener("report-list:ver-detalle", (e) => {
			const { reportId } = e.detail;
			router.navigate(`/detail/${reportId}`);
		});
	}

	/* ── Subscriptions ─────────────────────────────────────────────────── */

	static _setupSubscriptions() {
		// Reports → update markers
		subscribe("reports", (reports) => {
			const mapViewport = document.querySelector("map-viewport");
			if (mapViewport) {
				mapViewport.updateReports(reports);
			}
		});

		// Filters → re-fetch. The radius lives here too, and it decides how wide
		// the nearby query is, so both areas are refreshed.
		subscribe("filters", (filters) => {
			const mapViewport = document.querySelector("map-viewport");
			if (mapViewport) {
				updateBounds(mapViewport.getBounds(), filters.selectedCategories);
			}
			updateNearby(state.ui?.userLocation ?? null, filters.radiusKm ?? null);
		});

		// Selected report → fly to marker.
		// The "ui" slice also carries userLocation, which GPS updates rewrite
		// every few seconds; without this guard every fix re-ran flyTo() (an
		// 0.8s animation) and re-opened the popup, so the map shook and the
		// popup flickered for as long as a report stayed selected.
		// The nearby area follows the user. It is seeded right here instead of
		// waiting for a change event: on a cold start the first GPS fix can land
		// BEFORE these subscriptions exist, and then "userLocation changed" never
		// fires again while the user stands still — the list would stay empty.
		let lastUserLocation = state.ui?.userLocation ?? null;
		updateNearby(lastUserLocation, state.filters?.radiusKm ?? null, {
			fromGps: lastUserLocation !== null,
		});

		subscribe("ui", (ui) => {
			if (ui.userLocation !== lastUserLocation) {
				lastUserLocation = ui.userLocation;
				updateNearby(ui.userLocation, state.filters?.radiusKm ?? null, {
					fromGps: true,
				});
			}
		});

		let lastSelectedReportId = state.ui?.selectedReportId ?? null;
		subscribe("ui", (ui) => {
			const selected = ui.selectedReportId ?? null;
			if (selected === lastSelectedReportId) return;
			lastSelectedReportId = selected;

			if (!selected) return;
			const mapViewport = document.querySelector("map-viewport");
			if (mapViewport) {
				mapViewport.flyToReport(selected);
			}
		});

		// Theme changes → update map
		onThemeChange((theme) => {
			dispatch("SET_THEME", theme);
		});
	}
}
